import { useEffect } from "react";

/**
 * Inject one or more JSON-LD structured-data scripts into <head>.
 *
 * Each entry is identified by `key` so we can update / remove the exact tag
 * on route change without touching other JSON-LD blocks.
 *
 * Usage:
 *   usePageJsonLd({ key: "video", data: { "@context": "https://schema.org", ... } });
 */
export default function usePageJsonLd({ key, data } = {}) {
  useEffect(() => {
    if (!key) return undefined;
    const selector = `script[type="application/ld+json"][data-jsonld="${key}"]`;
    let el = document.head.querySelector(selector);

    if (!data) {
      if (el) el.remove();
      return undefined;
    }

    if (!el) {
      el = document.createElement("script");
      el.setAttribute("type", "application/ld+json");
      el.setAttribute("data-jsonld", key);
      document.head.appendChild(el);
    }
    try {
      el.textContent = JSON.stringify(data);
    } catch {
      /* ignore serialization errors */
    }

    return () => {
      const stale = document.head.querySelector(selector);
      if (stale) stale.remove();
    };
  }, [key, data]);
}

/** Convert an integer number of seconds to ISO 8601 duration (PT#H#M#S). */
export function secondsToISODuration(total) {
  const s = Math.max(0, Math.floor(Number(total) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  let out = "PT";
  if (h) out += `${h}H`;
  if (m) out += `${m}M`;
  if (sec || (!h && !m)) out += `${sec}S`;
  return out;
}
